import { Extension, type Range } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { Suggestion, type SuggestionProps } from "@tiptap/suggestion";

/** Live state of the "]" field-insert trigger inside the edit session. */
export interface FieldSuggestionState {
  /** The filter text typed after the "]" so far. */
  query: string;
  /** The doc range covering the "]" and the query, replaced on insert. */
  range: Range;
  /** Viewport rect of the "]" decoration, for positioning the menu. */
  clientRect: DOMRect | null;
}

/** Options wiring the suggestion trigger to the session overlay. */
export interface FieldSuggestionOptions {
  /** Reports the trigger state on open/typing, and null when it closes. */
  onState: (state: FieldSuggestionState | null) => void;
}

/**
 * TipTap extension driving the field menu: typing "]" (anywhere, any
 * prefix) opens a suggestion whose query/range/anchor rect are reported to
 * the session overlay, which renders the existing FieldSlashMenu.
 */
export const FieldSuggestion = Extension.create<FieldSuggestionOptions>({
  name: "fieldSuggestion",

  addOptions() {
    return {
      onState: () => undefined,
    };
  },

  addProseMirrorPlugins() {
    const { onState } = this.options;
    const report = (props: SuggestionProps<unknown, unknown>) =>
      onState({
        query: props.query,
        range: props.range,
        clientRect: props.clientRect ? props.clientRect() : null,
      });
    return [
      Suggestion<unknown, unknown>({
        pluginKey: new PluginKey("fieldSuggestion"),
        editor: this.editor,
        char: "]",
        allowSpaces: false,
        allowedPrefixes: null,
        startOfLine: false,
        items: () => [],
        render: () => ({
          onStart: report,
          onUpdate: report,
          onExit: () => onState(null),
        }),
      }),
    ];
  },
});
